import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { buildPhotoUrl } from 'utils/photoUrl';

const DetailPanel = ({ request, onClose, onUpdateStatus }) => {
  const [selectedStatus, setSelectedStatus] = useState(request?.status);

  // Sync status when request changes
  useEffect(() => {
    setSelectedStatus(request?.status);
  }, [request?.status]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleStatusUpdate = () => {
    if (selectedStatus !== request?.status) {
      onUpdateStatus(request?.id, selectedStatus);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      'in-progress': 'bg-primary/10 text-primary border-primary/20',
      completed: 'bg-success/10 text-success border-success/20',
      cancelled: 'bg-muted text-muted-foreground border-border'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      'in-progress': 'Activity',
      completed: 'CheckCircle2',
      cancelled: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose(); }}
    >
      {/* Modal panel */}
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={18} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Request Details</h2>
              <p className="text-xs text-muted-foreground">{request?.request_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">

          {/* Status row */}
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Select
                label="Status"
                options={statusOptions}
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value)}
              />
            </div>
            <div className="pt-6">
              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(request?.status)}`}>
                <Icon name={getStatusIcon(request?.status)} size={12} strokeWidth={2} />
                <span className="capitalize">{request?.status?.replace('-', ' ')}</span>
              </span>
            </div>
          </div>
          {selectedStatus !== request?.status && (
            <Button
              variant="default"
              size="sm"
              className="w-full -mt-4"
              onClick={handleStatusUpdate}
            >
              Update Status
            </Button>
          )}

          {/* Two-column grid for main details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

            {/* Driver Information */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Icon name="User" size={13} strokeWidth={2} />
                Driver Information
              </h4>
              <div className="space-y-2">
                {request?.driver_name && (
                  <div className="flex items-center space-x-2">
                    <Icon name="User" size={15} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-sm text-foreground font-medium">{request?.driver_name}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={15} color="var(--color-primary)" strokeWidth={2} />
                  <span className="text-sm text-foreground">{request?.driver_phone || '—'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Truck" size={15} color="var(--color-primary)" strokeWidth={2} />
                  <span className="text-sm text-foreground">Unit: {request?.unit_number || '—'}</span>
                </div>
                {request?.vin && (
                  <div className="flex items-center space-x-2">
                    <Icon name="Hash" size={15} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-sm text-foreground font-mono text-xs">{request?.vin}</span>
                  </div>
                )}
                {request?.yard_name && (
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={15} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-sm text-foreground">{request?.yard_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Request Details */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Icon name="Wrench" size={13} strokeWidth={2} />
                Request Details
              </h4>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Urgency</p>
                  <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-xs font-medium border ${
                    request?.urgency === 'Emergency' ?'bg-destructive/10 text-destructive border-destructive/20' :'bg-secondary/10 text-secondary border-secondary/20'
                  }`}>
                    <Icon
                      name={request?.urgency === 'Emergency' ? 'AlertTriangle' : 'Calendar'}
                      size={11}
                      strokeWidth={2}
                    />
                    <span>{request?.urgency || '—'}</span>
                  </span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Issue Type</p>
                  <p className="text-sm text-foreground">{request?.issue_type || '—'}</p>
                </div>
                {request?.mileage != null && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Mileage</p>
                    <p className="text-sm text-foreground">{request?.mileage?.toLocaleString()} mi</p>
                  </div>
                )}
                {request?.engine_hours != null && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Engine Hours</p>
                    <p className="text-sm text-foreground">{request?.engine_hours?.toLocaleString()} hrs</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {request?.description && (
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Icon name="AlignLeft" size={13} strokeWidth={2} />
                Description
              </h4>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{request?.description}</p>
            </div>
          )}

          {/* Parts Needed */}
          {request?.parts_needed && (
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Icon name="Package" size={13} strokeWidth={2} />
                Parts / Supplies Needed
              </h4>
              <p className="text-sm text-foreground">{request?.parts_needed}</p>
            </div>
          )}

          {/* Photos */}
          {request?.photo_urls && request?.photo_urls?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Icon name="Camera" size={13} strokeWidth={2} />
                Photos ({request?.photo_urls?.length})
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {request?.photo_urls?.map((url, index) => (
                  <a key={index} href={buildPhotoUrl(url)} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors">
                      <img
                        src={buildPhotoUrl(url)}
                        alt={`Service request photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Location */}
          {request?.gps_latitude && request?.gps_longitude && (
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Icon name="Navigation" size={13} strokeWidth={2} />
                Location
              </h4>
              <div className="space-y-2">
                {request?.gps_address && (
                  <div className="flex items-start space-x-2">
                    <Icon name="MapPin" size={15} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{request?.gps_address}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Icon name="Navigation" size={15} color="var(--color-primary)" strokeWidth={2} />
                  <span className="text-xs text-muted-foreground font-mono">
                    {request?.gps_latitude?.toFixed(6)}, {request?.gps_longitude?.toFixed(6)}
                  </span>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${request?.gps_latitude},${request?.gps_longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  <span>View on Google Maps</span>
                  <Icon name="ExternalLink" size={11} strokeWidth={2} />
                </a>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Icon name="Clock" size={13} strokeWidth={2} />
              Timeline
            </h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Plus" size={12} color="var(--color-primary)" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Submitted</p>
                  <p className="text-sm text-foreground">{formatDate(request?.created_at)}</p>
                </div>
              </div>
              {request?.updated_at && request?.updated_at !== request?.created_at && (
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="RefreshCw" size={12} color="var(--color-secondary)" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Updated</p>
                    <p className="text-sm text-foreground">{formatDate(request?.updated_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DetailPanel;